import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Loader2, Eye, EyeOff, CheckCircle2 } from "lucide-react";

export default function AdminReset() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("Password must be at least 6 characters");
    if (password !== confirm) return setError("Passwords don't match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/admin/login"), 2200);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not reset password");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F3F4F6] flex items-center justify-center px-6" data-testid="admin-reset-page">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-center mb-8">
          <Link to="/" data-testid="admin-logo-home" aria-label="Back to home">
            <img src="/upwardcast-logo-transparent.png" alt="UpwardCast" className="h-9 w-auto hover:opacity-80 transition-opacity" />
          </Link>
        </div>
        {!token ? (
          <div className="bg-white border border-[#0A0A0A] hard-shadow p-8 text-center" data-testid="reset-no-token">
            <p className="text-sm text-[#4B5563]">This reset link is invalid. Please request a new one from the sign in page.</p>
            <Link to="/admin/login" className="mt-6 inline-block text-sm font-semibold text-[#6D28B5] hover:text-[#4A1A7A]">Back to sign in</Link>
          </div>
        ) : done ? (
          <div className="bg-white border border-[#0A0A0A] hard-shadow p-8 text-center" data-testid="reset-done">
            <CheckCircle2 className="w-9 h-9 text-[#10B981] mx-auto mb-3" />
            <h1 className="text-xl font-bold tracking-tight mb-2">Password updated</h1>
            <p className="text-sm text-[#4B5563]">Redirecting you to sign in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-[#0A0A0A] hard-shadow p-8 space-y-5">
            <div>
              <h1 className="text-xl font-bold tracking-tight">Choose a new password</h1>
              <p className="text-sm text-[#4B5563] mt-1">Enter a new password for your host account.</p>
            </div>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2" data-testid="reset-error">
                {error}
              </div>
            )}
            <div className="relative">
              <input
                data-testid="reset-password-input"
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="New password"
                className="w-full border border-[#0A0A0A] px-4 py-3 pr-11 text-sm focus:outline-none focus:ring-2 focus:ring-[#6D28B5]"
              />
              <button type="button" onClick={() => setShowPw((s) => !s)} data-testid="reset-password-toggle"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#0A0A0A]">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <input
              data-testid="reset-confirm-input"
              type={showPw ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm new password"
              className="w-full border border-[#0A0A0A] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#6D28B5]"
            />
            <button type="submit" disabled={loading} data-testid="reset-submit-btn"
              className="w-full bg-[#6D28B5] text-white hover:bg-[#4A1A7A] disabled:opacity-50 px-6 py-3 font-semibold transition-colors flex items-center justify-center">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
